import React, { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'components/ui/button';
import { Textarea } from 'components/ui/textarea';
import { cn } from 'lib/utils';
import {
  FEEDBACK_COMMENT_MAX_LENGTH,
  FEEDBACK_RATINGS,
  FEEDBACK_TAGS,
  type FeedbackTag,
} from '../constants';
import type { Feedback, FeedbackInput } from '../types';

export interface FeedbackFormProps {
  /** The feedback already left, when the learner is editing it. */
  initial?: Feedback | null;
  pending: boolean;
  onSubmit: (input: FeedbackInput) => void;
  /** Shown as the quiet button beside Send; absent when there is nothing to postpone. */
  onLater?: () => void;
  laterLabel?: string;
}

const QUESTION = 'text-[14.5px] font-semibold leading-snug text-foreground';

/** 1–5 as five touch-sized buttons: a radio group, so assistive technology reads it as one choice. */
function RatingScale({
  labelId,
  value,
  onChange,
  disabled,
}: {
  labelId: string;
  value: number | undefined;
  onChange: (next: number) => void;
  disabled: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div role="radiogroup" aria-labelledby={labelId} className="mt-2 grid grid-cols-5 gap-2">
      {FEEDBACK_RATINGS.map((rating) => {
        const selected = value === rating;
        return (
          <button
            key={rating}
            type="button"
            role="radio"
            aria-checked={selected}
            aria-label={t('feedback.rating_label', { value: rating })}
            disabled={disabled}
            onClick={() => onChange(rating)}
            className={cn(
              'h-11 rounded-lg border text-sm font-semibold tabular-nums transition-colors lg:h-10',
              'focus-visible:!outline-none focus-visible:ring-2 focus-visible:ring-foreground focus-visible:ring-offset-2',
              selected
                ? 'border-foreground bg-foreground text-background'
                : 'border-border bg-card text-muted-foreground hover:border-foreground/40 hover:text-foreground',
            )}
          >
            {rating}
          </button>
        );
      })}
    </div>
  );
}

/**
 * The two ratings, the quick reactions and the optional note. The same body serves the
 * dialog shown right after a test and the inline card on the result and history pages.
 * Sending needs only the first rating: not every learner can judge the second.
 */
export function FeedbackForm({ initial, pending, onSubmit, onLater, laterLabel }: FeedbackFormProps) {
  const { t } = useTranslation();
  const ids = useId();
  const [enjoyment, setEnjoyment] = useState<number | undefined>(initial?.enjoyment);
  const [examSimilarity, setExamSimilarity] = useState<number | undefined>(initial?.examSimilarity);
  const [tags, setTags] = useState<FeedbackTag[]>(initial?.tags ?? []);
  const [comment, setComment] = useState(initial?.comment ?? '');

  const toggleTag = (tag: FeedbackTag) =>
    setTags((current) => (current.includes(tag) ? current.filter((entry) => entry !== tag) : [...current, tag]));

  const canSend = enjoyment !== undefined && !pending;

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (enjoyment === undefined || pending) return;
    const note = comment.trim();
    onSubmit({
      enjoyment,
      ...(examSimilarity !== undefined && { examSimilarity }),
      tags,
      ...(note && { comment: note }),
    });
  };

  return (
    <form onSubmit={submit} className="space-y-5" noValidate>
      <div>
        <p id={`${ids}-enjoyment`} className={QUESTION}>
          {t('feedback.q_enjoyment')}
        </p>
        <RatingScale labelId={`${ids}-enjoyment`} value={enjoyment} onChange={setEnjoyment} disabled={pending} />
      </div>

      <div>
        <p id={`${ids}-similarity`} className={QUESTION}>
          {t('feedback.q_similarity')}
        </p>
        <RatingScale
          labelId={`${ids}-similarity`}
          value={examSimilarity}
          onChange={setExamSimilarity}
          disabled={pending}
        />
      </div>

      <div>
        <p id={`${ids}-tags`} className={QUESTION}>
          {t('feedback.highlights')}
        </p>
        <div role="group" aria-labelledby={`${ids}-tags`} className="mt-2 flex flex-wrap gap-2">
          {FEEDBACK_TAGS.map((tag) => {
            const selected = tags.includes(tag);
            return (
              <button
                key={tag}
                type="button"
                aria-pressed={selected}
                disabled={pending}
                onClick={() => toggleTag(tag)}
                className={cn(
                  'min-h-11 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors lg:min-h-8 lg:py-1',
                  'focus-visible:!outline-none focus-visible:ring-2 focus-visible:ring-foreground focus-visible:ring-offset-2',
                  selected
                    ? 'border-foreground bg-foreground text-background'
                    : 'border-border bg-card text-muted-foreground hover:border-foreground/40 hover:text-foreground',
                )}
              >
                {t(`feedback.tag_${tag}`)}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <Textarea
          aria-label={t('feedback.comment_placeholder')}
          placeholder={t('feedback.comment_placeholder')}
          value={comment}
          maxLength={FEEDBACK_COMMENT_MAX_LENGTH}
          rows={3}
          disabled={pending}
          onChange={(event) => setComment(event.target.value)}
        />
        <p className="mt-1 text-right text-xs tabular-nums text-muted-foreground" aria-hidden="true">
          {comment.length} / {FEEDBACK_COMMENT_MAX_LENGTH}
        </p>
      </div>

      <div className="flex flex-col-reverse gap-2.5 sm:flex-row sm:justify-end">
        {onLater && (
          <Button type="button" variant="outline" className="h-11 lg:h-9" disabled={pending} onClick={onLater}>
            {laterLabel ?? t('feedback.later')}
          </Button>
        )}
        <Button type="submit" className="h-11 lg:h-9" disabled={!canSend} aria-busy={pending}>
          {pending ? t('feedback.sending') : t('feedback.send')}
        </Button>
      </div>
    </form>
  );
}
